import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaSms, FaMobile, FaHeadset, FaCreditCard, FaCode } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchServicesSection } from '../repository/api';

const Services = () => {
  // Fetch services data from API
  const { data: servicesData, loading, error } = useApiData(fetchServicesSection);

  // Default fallback data
  const defaultServiceItems = [
    {
      id: 'bulksms',
      icon: <FaSms />,
      title: 'BULK SMS',
      description: 'Reach thousands of customers instantly with our reliable bulk SMS service.',
      features: [
        'High delivery rates',
        'Personalized messaging',
        'Detailed analytics',
        'Scheduled campaigns'
      ],
      color: '#2090B0'
    },
    {
      id: 'ussd',
      icon: <FaMobile />,
      title: 'USSD SERVICES',
      description: 'Create interactive mobile experiences with our USSD solutions.',
      features: [
        'Short codes provisioning',
        'Session management',
        'Menu builder',
        'Integration APIs'
      ],
      color: '#105090'
    },
    {
      id: 'callcenter',
      icon: <FaHeadset />,
      title: 'CALL CENTER SOLUTIONS',
      description: 'Enhance customer support with our advanced call center technology.',
      features: [
        'IVR system',
        'Call recording',
        'Real-time monitoring',
        'Performance analytics'
      ],
      color: '#2090B0'
    },
    {
      id: 'payments',
      icon: <FaCreditCard />,
      title: 'PAYMENT SOLUTIONS',
      description: 'Secure, fast, and reliable payment processing for your business.',
      features: [
        'Multiple payment methods',
        'Fraud protection',
        'Transaction reporting',
        'Recurring billing'
      ],
      color: '#105090'
    },
    {
      id: 'software',
      icon: <FaCode />,
      title: 'SOFTWARE DEVELOPMENT',
      description: 'Custom software solutions tailored to your business needs.',
      features: [
        'Web applications',
        'Mobile apps',
        'API integrations',
        'Cloud solutions'
      ],
      color: '#2090B0'
    },
    {
      id: 'api',
      icon: <FaCode />,
      title: 'API INTEGRATION',
      description: 'Custom software solutions tailored to your business needs.',
      features: [
        'Real-time data exchange',
        'Integration with existing systems',
        'Custom API development',
        'API documentation'
      ],
      color: '#105090'
    }
  ];

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    if (!apiData || !apiData.services) return defaultServiceItems;
    
    const iconMap = {
      'BULK SMS': <FaSms />,
      'USSD SERVICES': <FaMobile />,
      'CALL CENTER SOLUTIONS': <FaHeadset />,
      'PAYMENT SOLUTIONS': <FaCreditCard />,
      'SOFTWARE DEVELOPMENT': <FaCode />,
      'API INTEGRATION': <FaCode />
    };

    const colorMap = {
      'BULK SMS': '#2090B0',
      'USSD SERVICES': '#105090',
      'CALL CENTER SOLUTIONS': '#2090B0',
      'PAYMENT SOLUTIONS': '#105090',
      'SOFTWARE DEVELOPMENT': '#2090B0',
      'API INTEGRATION': '#105090'
    };

    return apiData.services.map((service, index) => ({
      id: service.service_name.toLowerCase().replace(/\s+/g, ''),
      icon: iconMap[service.service_name] || <FaCode />,
      title: service.service_name,
      description: service.brief_explanation,
      features: service.bullet_points || [],
      color: colorMap[service.service_name] || (index % 2 === 0 ? '#2090B0' : '#105090'),
      buttonText: service.button_text || 'Learn More'
    }));
  };

  // Use API data or fallback to default
  const serviceItems = servicesData ? transformApiData(servicesData) : defaultServiceItems;
  const sectionHeading = servicesData?.overall_heading || 'Our Services';
  const sectionDescription = servicesData?.overall_explanation || 'Comprehensive solutions to help your business grow and succeed';

  const shapes = [
    { className: "hexagon", color: "#105090", top: "15%", right: "10%", size: "120px", id: "shape-1" },
    { className: "circle", color: "#2090B0", top: "65%", left: "5%", size: "80px", id: "shape-2" },
    { className: "triangle", color: "#105090", top: "25%", left: "15%", size: "100px", id: "shape-3" },
    { className: "square", color: "#2090B0", bottom: "10%", right: "8%", size: "90px", id: "shape-4" }
  ];

  // Show loading state while fetching data
  if (loading) {
    return (
      <ServicesSection id="services">
        <SectionHeader>
          <SectionTitle>
            Our <HighlightText>Services</HighlightText>
          </SectionTitle>
          <SectionDescription>
            Loading services...
          </SectionDescription>
        </SectionHeader>
      </ServicesSection>
    );
  }

  // Show error state if API call failed
  if (error) {
    console.error('Services section error:', error);
    // Continue with default data instead of showing error
  }

  return (
    <ServicesSection id="services">
      <BackgroundShapes>
        {shapes.map((shape, index) => (
          <GeometricShape 
            key={shape.id}
            className={shape.className} 
            color={shape.color} 
            top={shape.top}
            left={shape.left}
            right={shape.right}
            bottom={shape.bottom}
            size={shape.size}
          />
        ))}
      </BackgroundShapes>
      
      <SectionHeader>
        <SectionTitle>
          {sectionHeading.includes('Services') ? (
            <>
              {sectionHeading.replace('Services', '').trim()} <HighlightText>Services</HighlightText>
            </>
          ) : (
            sectionHeading
          )}
        </SectionTitle>
        
        <SectionDescription>
          {sectionDescription}
        </SectionDescription>
      </SectionHeader>

      <SectionSubtitle>
        Explore Our <HighlightText>Service Offerings</HighlightText>
      </SectionSubtitle>

      <ServiceGrid>
        {serviceItems.map((service, index) => (
          <ServiceCard 
            key={service.id}
            color={service.color}
            delay={index * 0.02}
          >
            <ServiceIconWrapper color={service.color}>
              {service.icon}
              <IconBackdrop />
            </ServiceIconWrapper>
            
            <ServiceTitle delay={index * 0.05 + 0.05}>
              {service.title}
            </ServiceTitle>
            
            <ServiceDescription delay={index * 0.05 + 0.1}>
              {service.description}
            </ServiceDescription>
            
            <FeaturesList>
              {service.features.map((feature, idx) => (
                <FeatureItem 
                  key={idx} 
                  delay={idx * 0.01}
                  itemDelay={index * 0.01 + idx * 0.01}
                >
                  <FeaturePoint color={service.color} />
                  {feature}
                </FeatureItem>
              ))}
            </FeaturesList>
            
            <ServiceButton 
              color={service.color} 
              delay={index * 0.02 + 0.05}
            >
              {service.buttonText || 'Learn More'}
            </ServiceButton>
          </ServiceCard>
        ))}
      </ServiceGrid>
    </ServicesSection>
  );
};

export default Services;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px); // Reduced from 30px
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px); // Reduced from -30px
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px); // Reduced from 30px
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9); // Less dramatic scaling
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05); // Reduced from 1.1
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const breathe = keyframes`
  0% {
    opacity: 0.2;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.05) rotate(3deg); // Reduced scaling and rotation
  }
  100% {
    opacity: 0.2;
    transform: scale(1) rotate(0deg);
  }
`;

// === Styled Components ===
const ServicesSection = styled.section`
  padding: 120px 40px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fb 0%,rgb(162, 165, 171) 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const GeometricShape = styled.div`
  position: absolute;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  width: ${props => props.size};
  height: ${props => props.size};
  background-color: ${props => props.color};
  opacity: 0.05;
  z-index: 0;
  animation: ${breathe} ${props => 8 + Math.random() * 6}s infinite ease-in-out;
  
  &.circle {
    border-radius: 50%;
  }
  
  &.square {
    transform: rotate(45deg);
  }
  
  &.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  &.hexagon {
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  width: 100%;
  max-width: 800px;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 20px;
  color: #666;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.4s ease-out 0.1s;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 40px;
  text-align: center;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease; // Reduced from 0.4s
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  z-index: 2;
  animation: ${fadeIn} 0.3s ease ${props => props.delay}s;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: ${props => props.color};
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    
    &:before {
      width: 100%;
      opacity: 0.05;
    }
  }
`;

const ServiceIconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${props => `${props.color}10`};
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 25px;
  position: relative;
  transition: all 0.3s ease;
  animation: ${fadeInScale} 0.25s ease 0.1s;
  
  svg {
    z-index: 2;
  }
`;

const IconBackdrop = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  z-index: 1;
  animation: ${pulse} 2s infinite ease-in-out; // Faster pulse
  animation-play-state: running;
`;

const ServiceTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.3s ease ${props => props.delay}s;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ServiceDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 25px;
  flex-grow: 1;
  animation: ${fadeIn} 0.3s ease ${props => props.delay}s;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 15px;
  margin-bottom: 12px;
  animation: ${fadeInLeft} 0.25s ease ${props => props.itemDelay}s;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FeaturePoint = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 12px;
  flex-shrink: 0;
`;

const ServiceButton = styled.button`
  background: transparent;
  color: ${props => props.color};
  border: 1px solid ${props => props.color};
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  animation: ${fadeIn} 0.3s ease ${props => props.delay}s;
  
  &:hover {
    background: ${props => props.color};
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px ${props => props.color}30;
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;